import React, { forwardRef, useMemo } from 'react';
import NumberInput from 'components/base/number_input';
import DateTimeInput from 'components/base/datetime_input';
import styles from 'src/webhook_notifications';

const textOperators = [
  'eq',
  'ne',
  'contain',
  'not_contain',
  'start_with',
  'end_with',
];
const numberOperators = ['eq', 'ne', 'le', 'lt', 'ge', 'gt'];
const multiSelectOperators = ['include', 'not_include'];
const fieldOperators = {
  SINGLE_LINE_TEXT: textOperators,
  MULTI_LINE_TEXT: textOperators,
  RICH_TEXT: textOperators,
  RADIO_BUTTON: multiSelectOperators,
  DROP_DOWN: multiSelectOperators,
  STATUS: multiSelectOperators,
  LINK: textOperators,
  NUMBER: numberOperators,
  CHECK_BOX: multiSelectOperators,
  MULTI_SELECT: multiSelectOperators,
  CREATOR: multiSelectOperators,
  MODIFIER: multiSelectOperators,
  USER_SELECT: multiSelectOperators,
  STATUS_ASSIGNEE: multiSelectOperators,
  ORGANIZATION_SELECT: multiSelectOperators,
  GROUP_SELECT: multiSelectOperators,
  DATE: numberOperators,
  TIME: numberOperators,
  DATETIME: numberOperators,
  COMMENT_MENTION: multiSelectOperators,
};

const operatorLabels = {
  eq: '等しい',
  ne: '等しくない',
  le: '以下',
  lt: 'より小さい',
  ge: '以上',
  gt: 'より大きい',
  contain: '次の文字列を含む',
  not_contain: '次の文字列を含まない',
  include: '次のいずれかを含む',
  not_include: '次のいずれも含まない',
  start_with: '次の文字列で始まる',
  end_with: '次の文字列で終わる',
};

const relativeUnits = [
  { value: 'day', label: '日' },
  { value: 'week', label: '週' },
  { value: 'month', label: '月' },
  { value: 'year', label: '年' },
];
const relativeDirections = [
  { value: 'before', label: '前' },
  { value: 'after', label: '後' },
];
const weekdayOptions = [
  { value: 'all', label: 'すべての曜日' },
  { value: 'sun', label: '日' },
  { value: 'mon', label: '月' },
  { value: 'tue', label: '火' },
  { value: 'wed', label: '水' },
  { value: 'thu', label: '木' },
  { value: 'fri', label: '金' },
  { value: 'sat', label: '土' },
];
const monthDayOptions = [
  { value: 'all', label: 'すべて' },
  ...Array.from({ length: 31 }, (_, index) => {
    const day = `${index + 1}`;
    return { value: day, label: `${index + 1}日` };
  }),
  { value: 'last', label: '末日' },
];

const dateValueOptionDefinitions = [
  {
    value: 'absolute_date',
    label: '日付を指定',
    supports: ['DATE'],
    kind: 'absolute_date',
    usesPlainValue: true,
  },
  {
    value: 'absolute_datetime',
    label: '日付時刻を指定',
    supports: ['DATETIME'],
    kind: 'absolute_datetime',
    usesPlainValue: true,
  },
  {
    value: 'relative_today_from',
    label: '今日から',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_today_from',
    defaultConfig: {
      kind: 'relative_today_from',
      amount: '1',
      unit: 'day',
      direction: 'after',
    },
  },
  { divider: true },
  {
    value: 'relative_day:yesterday',
    label: '昨日',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_day',
    preset: 'yesterday',
    defaultConfig: { kind: 'relative_day', preset: 'yesterday' },
  },
  {
    value: 'relative_day:today',
    label: '今日',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_day',
    preset: 'today',
    defaultConfig: { kind: 'relative_day', preset: 'today' },
  },
  {
    value: 'relative_day:tomorrow',
    label: '明日',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_day',
    preset: 'tomorrow',
    defaultConfig: { kind: 'relative_day', preset: 'tomorrow' },
  },
  { divider: true },
  {
    value: 'relative_week:last_week',
    label: '先週',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_week',
    preset: 'last_week',
    defaultConfig: {
      kind: 'relative_week',
      preset: 'last_week',
      weekday: 'all',
    },
  },
  {
    value: 'relative_week:this_week',
    label: '今週',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_week',
    preset: 'this_week',
    defaultConfig: {
      kind: 'relative_week',
      preset: 'this_week',
      weekday: 'all',
    },
  },
  {
    value: 'relative_week:next_week',
    label: '来週',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_week',
    preset: 'next_week',
    defaultConfig: {
      kind: 'relative_week',
      preset: 'next_week',
      weekday: 'all',
    },
  },
  { divider: true },
  {
    value: 'relative_month:last_month',
    label: '先月',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_month',
    preset: 'last_month',
    defaultConfig: {
      kind: 'relative_month',
      preset: 'last_month',
      day: 'all',
    },
  },
  {
    value: 'relative_month:this_month',
    label: '今月',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_month',
    preset: 'this_month',
    defaultConfig: {
      kind: 'relative_month',
      preset: 'this_month',
      day: 'all',
    },
  },
  {
    value: 'relative_month:next_month',
    label: '来月',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_month',
    preset: 'next_month',
    defaultConfig: {
      kind: 'relative_month',
      preset: 'next_month',
      day: 'all',
    },
  },
  { divider: true },
  {
    value: 'relative_year:last_year',
    label: '昨年',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_year',
    preset: 'last_year',
    defaultConfig: { kind: 'relative_year', preset: 'last_year' },
  },
  {
    value: 'relative_year:this_year',
    label: '今年',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_year',
    preset: 'this_year',
    defaultConfig: { kind: 'relative_year', preset: 'this_year' },
  },
  {
    value: 'relative_year:next_year',
    label: '来年',
    supports: ['DATE', 'DATETIME'],
    kind: 'relative_year',
    preset: 'next_year',
    defaultConfig: { kind: 'relative_year', preset: 'next_year' },
  },
  { divider: true },
  {
    value: 'current_time',
    label: '当時刻',
    supports: ['DATETIME'],
    kind: 'current_time',
    defaultConfig: { kind: 'current_time' },
  },
];

const defaultOptionForField = (fieldType) => {
  return fieldType === 'DATETIME' ? 'absolute_datetime' : 'absolute_date';
};

const getDateValueOptions = (fieldType) => {
  const options = [];
  let pendingDivider = false;
  dateValueOptionDefinitions.forEach((definition) => {
    if (definition.divider) {
      pendingDivider = true;
      return;
    }
    if (definition.supports && !definition.supports.includes(fieldType)) {
      return;
    }
    if (pendingDivider && options.length > 0) {
      options.push({ divider: true });
    }
    pendingDivider = false;
    options.push(definition);
  });
  return options;
};

const getOptionDefinition = (value) => {
  return dateValueOptionDefinitions.find((definition) => {
    return !definition.divider && definition.value === value;
  });
};

const getOptionValueFromConfig = (fieldType, config) => {
  if (!config?.kind) {
    return null;
  }
  const definition = dateValueOptionDefinitions.find((option) => {
    if (option.divider) return false;
    if (option.supports && !option.supports.includes(fieldType)) return false;
    if (option.kind !== config.kind) return false;
    if (option.preset && option.preset !== config.preset) return false;
    return true;
  });
  return definition?.value || null;
};

const getDefaultConfigForOption = (definition, currentConfig) => {
  if (!definition) {
    return null;
  }
  if (
    currentConfig &&
    currentConfig.kind === definition.kind &&
    (!definition.preset || currentConfig.preset === definition.preset)
  ) {
    return { ...currentConfig };
  }
  if (
    definition.kind === 'relative_week' &&
    currentConfig?.kind === 'relative_week'
  ) {
    return {
      kind: 'relative_week',
      preset: definition.preset,
      weekday: currentConfig.weekday || 'all',
    };
  }
  if (
    definition.kind === 'relative_month' &&
    currentConfig?.kind === 'relative_month'
  ) {
    return {
      kind: 'relative_month',
      preset: definition.preset,
      day: currentConfig.day || 'all',
    };
  }
  if (definition.defaultConfig) {
    return { ...definition.defaultConfig };
  }
  return {
    kind: definition.kind,
    ...(definition.preset ? { preset: definition.preset } : {}),
  };
};

const parseDateConditionValue = (fieldType, rawValue) => {
  if (rawValue) {
    try {
      const parsed = JSON.parse(rawValue);
      if (parsed && typeof parsed === 'object' && parsed.kind) {
        const optionValue =
          getOptionValueFromConfig(fieldType, parsed) ||
          defaultOptionForField(fieldType);
        return {
          optionValue,
          config: parsed,
          usesPlainValue: false,
        };
      }
    } catch (e) {}
  }
  const optionValue = defaultOptionForField(fieldType);
  if (fieldType === 'DATETIME') {
    return {
      optionValue,
      config: {
        kind: 'absolute_datetime',
        datetime: rawValue || '',
      },
      usesPlainValue: true,
    };
  }
  return {
    optionValue,
    config: {
      kind: 'absolute_date',
      date: rawValue || '',
    },
    usesPlainValue: true,
  };
};

function ConditionValue({ field, condition, onChange }) {
  const value = condition.value;
  const values = useMemo(() => {
    try {
      return JSON.parse(
        value ||
          (field.options
            ? '[]'
            : field.type == 'COMMENT_MENTION'
              ? '["USER:"]'
              : '[""]'),
      );
    } catch (e) {}
    return [];
  }, [field, value]);
  const required = useMemo(() => {
    return ['le', 'lt', 'ge', 'gt'].includes(condition.operator);
  }, [condition]);

  if (!field) {
    return (
      <input
        className="form-control"
        type="text"
        value={''}
        onChange={(e) => {}}
      />
    );
  } else if (field.type == 'NUMBER') {
    return (
      <NumberInput
        className="form-control text-right"
        value={value || ''}
        onChange={onChange}
      />
    );
  } else if (field.type == 'DATE') {
    return (
      <DateConditionValue
        fieldType="DATE"
        value={value}
        required={required}
        onChange={onChange}
      />
    );
  } else if (field.type == 'TIME') {
    return (
      <div className="">
        <input
          className="form-control"
          type="time"
          required={required}
          value={value || ''}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      </div>
    );
  } else if (field.type == 'DATETIME') {
    return (
      <DateConditionValue
        fieldType="DATETIME"
        value={value}
        required={required}
        onChange={onChange}
      />
    );
  } else if (
    [
      'CREATOR',
      'MODIFIER',
      'USER_SELECT',
      'STATUS_ASSIGNEE',
      'ORGANIZATION_SELECT',
      'GROUP_SELECT',
    ].includes(field.type)
  ) {
    return (
      <div>
        {values.map((v, index) => (
          <div key={index} className={styles.conditionValue}>
            <input
              className="form-control"
              type="text"
              value={v || ''}
              onChange={(e) => {
                const newValues = [...values];
                newValues[index] = e.target.value;
                onChange(JSON.stringify(newValues));
              }}
            />
            {values.length > 1 && (
              <a
                href="#!"
                onClick={(e) => {
                  e.preventDefault();
                  const newValues = [...values];
                  newValues.splice(index, 1);
                  onChange(JSON.stringify(newValues));
                }}
              >
                <i className="material-icons mr-1" style={{ fontSize: '16px' }}>
                  remove_circle
                </i>
              </a>
            )}
          </div>
        ))}
        <div className="mt-2">
          <a
            href="#!"
            onClick={(e) => {
              e.preventDefault();
              onChange(JSON.stringify([...values, '']));
            }}
            className={styles.addValue}
          >
            <i className="material-icons mr-1">add_circle</i>
            <span>値を追加する</span>
          </a>
        </div>
      </div>
    );
  } else if (['COMMENT_MENTION'].includes(field.type)) {
    return (
      <div>
        {values.map((v, index) => {
          const [type, code] = v.split(':');
          return (
            <div key={index} className={styles.conditionValue}>
              <select
                className="form-control"
                value={type}
                onChange={(e) => {
                  const newValues = [...values];
                  newValues[index] = `${e.target.value}:${code}`;
                  onChange(JSON.stringify(newValues));
                }}
              >
                <option value="USER">ユーザー</option>
                <option value="ORGANIZATION">組織</option>
                <option value="GROUP">グループ</option>
              </select>
              <input
                className="form-control"
                type="text"
                value={code || ''}
                onChange={(e) => {
                  const newValues = [...values];
                  newValues[index] = `${type}:${e.target.value}`;
                  onChange(JSON.stringify(newValues));
                }}
              />
              {values.length > 1 && (
                <a
                  href="#!"
                  onClick={(e) => {
                    e.preventDefault();
                    const newValues = [...values];
                    newValues.splice(index, 1);
                    onChange(JSON.stringify(newValues));
                  }}
                >
                  <i
                    className="material-icons mr-1"
                    style={{ fontSize: '16px' }}
                  >
                    remove_circle
                  </i>
                </a>
              )}
            </div>
          );
        })}
        <div className="mt-2">
          <a
            href="#!"
            onClick={(e) => {
              e.preventDefault();
              onChange(JSON.stringify([...values, 'USER:']));
            }}
            className={styles.addValue}
          >
            <i className="material-icons mr-1">add_circle</i>
            <span>値を追加する</span>
          </a>
        </div>
      </div>
    );
  } else if (['include', 'not_include'].includes(condition.operator)) {
    const options = field.options || [];
    return (
      <div className={styles.multiSelectWrapper}>
        {field.type !== 'STATUS' && (
          <div>
            <label
              className={`checkbox-wrapper ${styles.checkboxWrapper} ${styles.textNowrapEllipsis}`}
            >
              <input
                className={styles.flexNone}
                type="checkbox"
                checked={values.includes('') || false}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange(JSON.stringify([...values, '']));
                  } else {
                    onChange(JSON.stringify(values.filter((v) => v != '')));
                  }
                }}
              />
              --
            </label>
          </div>
        )}
        {options.map((opt) => (
          <div key={opt}>
            <label
              className={`checkbox-wrapper ${styles.checkboxWrapper} ${styles.textNowrapEllipsis}`}
            >
              <input
                className={styles.flexNone}
                type="checkbox"
                checked={values.includes(opt) || false}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange(JSON.stringify([...values, opt]));
                  } else {
                    onChange(JSON.stringify(values.filter((v) => v != opt)));
                  }
                }}
              />
              {opt}
            </label>
          </div>
        ))}
      </div>
    );
  } else {
    return (
      <input
        className="form-control"
        type="text"
        value={value || ''}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    );
  }
}

function DateConditionValue({ fieldType, value, required, onChange }) {
  const parsed = useMemo(() => {
    return parseDateConditionValue(fieldType, value);
  }, [fieldType, value]);
  const options = useMemo(() => {
    return getDateValueOptions(fieldType);
  }, [fieldType]);
  const selectedDefinition = getOptionDefinition(parsed.optionValue);

  const handleTypeChange = (newValue) => {
    if (newValue === parsed.optionValue) {
      return;
    }
    const definition = getOptionDefinition(newValue);
    if (!definition) {
      return;
    }
    if (definition.usesPlainValue) {
      onChange('');
      return;
    }
    const config = getDefaultConfigForOption(definition);
    onChange(JSON.stringify(config));
  };

  const updateConfig = (updates) => {
    if (!selectedDefinition) {
      return;
    }
    const baseConfig =
      parsed.config &&
      parsed.config.kind === selectedDefinition.kind &&
      (!selectedDefinition.preset ||
        parsed.config.preset === selectedDefinition.preset)
        ? parsed.config
        : getDefaultConfigForOption(selectedDefinition);
    const nextConfig = { ...baseConfig, ...updates };
    onChange(JSON.stringify(nextConfig));
  };

  const absoluteDateInputValue =
    (parsed.config && parsed.config.date) || value || '';
  const absoluteDatetimeValue =
    (parsed.config && parsed.config.datetime) || value || '';

  const renderExtraInputs = () => {
    if (!selectedDefinition) {
      return null;
    }
    if (selectedDefinition.usesPlainValue) {
      if (selectedDefinition.value === 'absolute_date') {
        return (
          <input
            className="form-control"
            type="date"
            required={required}
            value={absoluteDateInputValue}
            onChange={(e) => {
              onChange(e.target.value);
            }}
          />
        );
      }
      if (selectedDefinition.value === 'absolute_datetime') {
        return (
          <DateTimeInput
            className="form-control"
            required={required}
            value={absoluteDatetimeValue}
            onChange={onChange}
          />
        );
      }
      return null;
    }
    const config = parsed.config || selectedDefinition.defaultConfig;
    switch (selectedDefinition.kind) {
      case 'relative_today_from':
        return (
          <div className={styles.conditionValue}>
            <input
              type="number"
              min="1"
              className="form-control"
              value={config?.amount || '1'}
              onChange={(e) => {
                let amount = e.target.value.replace(/\D/g, '');
                if (!amount || amount === '0') {
                  amount = '1';
                }
                updateConfig({
                  amount,
                });
              }}
            />
            <select
              className="form-control"
              value={config?.unit || 'day'}
              onChange={(e) => {
                updateConfig({ unit: e.target.value });
              }}
            >
              {relativeUnits.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
            <select
              className="form-control"
              value={config?.direction || 'after'}
              onChange={(e) => {
                updateConfig({ direction: e.target.value });
              }}
            >
              {relativeDirections.map((direction) => (
                <option key={direction.value} value={direction.value}>
                  {direction.label}
                </option>
              ))}
            </select>
          </div>
        );
      case 'relative_week':
        return (
          <select
            className="form-control"
            value={config?.weekday || 'all'}
            onChange={(e) => {
              updateConfig({ weekday: e.target.value });
            }}
          >
            {weekdayOptions.map((weekday) => (
              <option key={weekday.value} value={weekday.value}>
                {weekday.label}
              </option>
            ))}
          </select>
        );
      case 'relative_month':
        return (
          <select
            className="form-control"
            value={config?.day || 'all'}
            onChange={(e) => {
              updateConfig({ day: e.target.value });
            }}
          >
            {monthDayOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case 'relative_day':
      case 'relative_year':
      case 'current_time':
      default:
        return null;
    }
  };

  return (
    <div>
      <select
        className="form-control mb-2"
        value={parsed.optionValue}
        onChange={(e) => handleTypeChange(e.target.value)}
      >
        {options.map((option, index) => {
          if (option.divider) {
            return (
              <option
                key={`divider-${index}`}
                disabled
                value={`divider-${index}`}
              >
                ---
              </option>
            );
          }
          return (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          );
        })}
      </select>
      {renderExtraInputs()}
    </div>
  );
}

export default function Condition({
  condition,
  conditionFields,
  onChange,
  onRemove,
}) {
  const field = useMemo(() => {
    return conditionFields.find((field) => {
      return field.code === condition.source;
    });
  }, [condition.source, conditionFields]);
  const operators = useMemo(() => {
    return (field?.type ? fieldOperators[field?.type] : undefined) || [];
  }, [field]);

  const onConditionSourceChange = (e) => {
    const source = e.target.value;
    const field = conditionFields.find((field) => {
      return field.code === source;
    });
    if (!field) {
      return;
    }
    const operator = fieldOperators[field?.type][0];
    onChange({ ...condition, source: e.target.value, operator, value: '' });
  };

  const onConditionOperatorChange = (e) => {
    onChange({ ...condition, operator: e.target.value });
  };

  const onConditionValueChange = (value) => {
    onChange({ ...condition, value });
  };
  return (
    <div className="list-group-item list-group-item-action">
      <div className="row align-items-start">
        <div className="col-sm-4">
          <select
            className="form-control"
            value={condition.source}
            onChange={onConditionSourceChange}
          >
            <option value=""></option>
            {conditionFields.map((field) => {
              return (
                <option key={field.code} value={field.code}>
                  {field.label}
                </option>
              );
            })}
          </select>
        </div>
        <div className="col-sm-3">
          <select
            className="form-control"
            value={condition.operator}
            onChange={onConditionOperatorChange}
          >
            {operators.map((operator) => {
              return (
                <option key={operator} value={operator}>
                  {operatorLabels[operator]}
                </option>
              );
            })}
          </select>
        </div>
        <div className="col-sm-4">
          <ConditionValue
            field={field}
            condition={condition}
            onChange={onConditionValueChange}
          />
        </div>
        <div className="col-sm-1 align-self-center ">
          <a
            href="#!"
            onClick={(e) => {
              e.preventDefault();
              onRemove(e);
            }}
          >
            <i className="material-icons mr-1">remove_circle</i>
          </a>
        </div>
      </div>
    </div>
  );
}
